import type { History } from 'history';

export const COMPLIANCE_COMPOSER_FORM_SCROLL_ID = 'compliance-composer-form-scroll';
export const COMPLIANCE_COMPOSER_PREVIEW_SCROLL_ID = 'compliance-composer-preview-scroll';

export type ComplianceComposerRestore = {
  formScroll: number,
  previewScroll: number,
  mobileSection: 'compose' | 'preview',
};

export type ComplianceComposerLocationState = {
  restore?: ComplianceComposerRestore,
};

/** Held across ActivityLogs / other tabs so Back can restore if history state is dropped. */
let pendingComplianceComposerRestore: ComplianceComposerRestore | null = null;

export const peekPendingComplianceComposerRestore = (): ComplianceComposerRestore | null => (
  pendingComplianceComposerRestore
);

export const consumePendingComplianceComposerRestore = (): ComplianceComposerRestore | null => {
  const restore = pendingComplianceComposerRestore;
  pendingComplianceComposerRestore = null;
  return restore;
};

export const clearPendingComplianceComposerRestore = () => {
  pendingComplianceComposerRestore = null;
};

export const holdComplianceComposerRestore = (restore: ComplianceComposerRestore) => {
  pendingComplianceComposerRestore = restore;
};

const readScrollById = (id: string): number => (
  document.getElementById(id)?.scrollTop ?? 0
);

export const applyComplianceComposerScrollById = (id: string, top: number) => {
  const el = document.getElementById(id);
  if (el) el.scrollTop = top;
};

export const snapshotComplianceComposerRestore = (
  ui: { mobileSection: 'compose' | 'preview' },
): ComplianceComposerRestore => ({
  formScroll: readScrollById(COMPLIANCE_COMPOSER_FORM_SCROLL_ID),
  previewScroll: readScrollById(COMPLIANCE_COMPOSER_PREVIEW_SCROLL_ID),
  mobileSection: ui.mobileSection === 'preview' ? 'preview' : 'compose',
});

export const hasComplianceComposerRestoreDepth = (
  restore?: ComplianceComposerRestore | null,
): boolean => Boolean(
  restore && (
    restore.formScroll > 0
    || restore.previewScroll > 0
    || restore.mobileSection === 'preview'
  ),
);

/**
 * Write form + preview scrollers onto the current /new URL so browser Back
 * from ActivityLogs (or another tab) reopens the same depths.
 */
export const stashComplianceComposerBeforeLeave = (
  history: History,
  ui: { mobileSection: 'compose' | 'preview' },
) => {
  const restore = snapshotComplianceComposerRestore(ui);
  pendingComplianceComposerRestore = restore;
  const prev = (history.location.state || {}) as ComplianceComposerLocationState;
  history.replace({
    pathname: history.location.pathname,
    search: history.location.search,
    state: {
      ...prev,
      restore,
    } as ComplianceComposerLocationState,
  });
};
